import { FC } from "react";
import {
  AdminSellBook
} from "Types";


interface Props {
  data: AdminSellBook;
}

const SellBooksViewForm: FC<Props> = ({ data }) => {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusBadge = (status: string) => {
    const base = "px-3 py-1 rounded-full text-sm font-medium";
    switch (status) {
      case "Pending":
        return `${base} bg-yellow-100 text-yellow-800`;
      case "Purchased":
        return `${base} bg-green-100 text-green-800`;
      case "Rejected":
        return `${base} bg-red-100 text-red-800`;
      default:
        return `${base} bg-gray-100 text-gray-800`;
    }
  };

  const getSellTypeBadge = (type?: string) => {
    const base = "px-3 py-1 rounded-full text-sm font-medium";
    switch (type) {
      case "Sell":
        return `${base} bg-blue-100 text-blue-800`;
      case "Donate":
        return `${base} bg-purple-100 text-purple-800`;
      default:
        return `${base} bg-gray-100 text-gray-600`;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Seller Full Name
            </label>
            <p className="mt-1 border-b pb-1">{data.fullName || "N/A"}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Mobile Number
            </label>
            <p className="mt-1 border-b pb-1">{data.mobileNumber || "N/A"}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <p className="mt-1 border-b pb-1">{data.emailId || "N/A"}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              ISBN
            </label>
            <p className="mt-1 border-b pb-1">{data.isbn || "N/A"}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Product Title
            </label>
            <p className="mt-1 border-b pb-1">{data.bookTitle || "N/A"}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Product Type
            </label>
            <p className="mt-1 border-b pb-1">{data.bookType || "N/A"}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Sell Type
            </label>
            <div className="mt-1">
              <span className={getSellTypeBadge(data.sellType)}>
                {data.sellType || "N/A"}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Preferred Store
            </label>
            <p className="mt-1 border-b pb-1">
              {data.preferredStoreToSell || "N/A"}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <div className="mt-1">
              <span className={getStatusBadge(data.status)}>
                {data.status || "N/A"}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Date
            </label>
            <p className="mt-1 border-b pb-1">{formatDate(data.createdAt)}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Last Updated
            </label>
            <p className="mt-1 border-b pb-1">
              {data.updatedAt ? formatDate(data.updatedAt) : "Not updated"}
            </p>
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-medium mb-4">Product Images</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.image1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image 1
              </label>
              <div className="border rounded-lg p-2">
                <img
                  src={data.image1}
                  alt={`${data.bookTitle || "Product"} - Image 1`}
                  className="w-full h-auto max-h-64 object-contain"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/300x400?text=Image+Not+Found";
                  }}
                />
              </div>
            </div>
          )}

          {data.image2 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image 2
              </label>
              <div className="border rounded-lg p-2">
                <img
                  src={data.image2}
                  alt={`${data.bookTitle || "Product"} - Image 2`}
                  className="w-full h-auto max-h-64 object-contain"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/300x400?text=Image+Not+Found";
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {!data.image1 && !data.image2 && (
          <p className="text-gray-500 italic">No images available</p>
        )}
      </div>
    </div>
  );
};

export default SellBooksViewForm;
